/**
 * selling/SellingTab.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Main selling dashboard — shows the user's listings grid with stats.
 *
 * WHAT LIVES HERE:
 *   - Stats bar (active listings, likes, followers, rating)
 *   - "View All Stats" modal trigger
 *   - "View All Items" modal trigger
 *   - Product grid using ItemCard components
 *   - Opens EditAdModal on card click
 *
 * SUB-COMPONENTS (each in their own file):
 *   ItemCard.jsx          — single listing card
 *   EditAdModal.jsx       — edit/delete/status toggle modal (~480 lines)
 *   sellingUtils.js       — shared constants and helpers
 *
 * PROPS (from ProfilePage sharedProps):
 *   user            {object}   — current user
 *   userProducts    {array}    — user's ads (from fetchMyAds via ProfilePage)
 *   loading         {boolean}  — true while initial data loads
 *   stats           {object}   — { activeListings, totalLikes, followers, rating, reviewCount }
 *   setStats        {function} — update stats after edit/delete
 *   setUserProducts {function} — update products after edit/delete
 *
 * LOADING STRATEGY:
 *   - Shows shimmer skeleton cards instead of a blocking spinner
 *   - Stats bar renders immediately with 0 placeholders (no layout shift)
 *   - Data arrives fast because ProfilePage now calls fetchMyAds() + fetchMyStats()
 *     in parallel via Promise.all — only the current user's ads are fetched
 * ─────────────────────────────────────────────────────────────────────────────
 */

import React, { useState } from 'react';
import styles from './SellingTab.module.css';
import ItemCard    from './ItemCard';
import EditAdModal from './EditAdModal';
import { StarRating } from './sellingUtils';

// ── Shimmer skeleton for a single card ───────────────────────────────────────
const SkeletonCard = () => (
  <div style={{
    borderRadius: 12, overflow: 'hidden',
    background: '#fff',
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
    display: 'flex', flexDirection: 'column',
  }}>
    <div style={{
      aspectRatio: '4 / 3',
      background: 'linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%)',
      backgroundSize: '200% 100%',
      animation: 'skeletonShimmer 1.4s ease-in-out infinite',
    }} />
    <div style={{ padding: '10px 12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ height: 11, borderRadius: 6, background: '#f0f0f0', backgroundSize: '200% 100%', animation: 'skeletonShimmer 1.4s ease-in-out infinite', width: '72%' }} />
      <div style={{ height: 13, borderRadius: 6, background: '#f0f0f0', backgroundSize: '200% 100%', animation: 'skeletonShimmer 1.4s ease-in-out infinite', width: '48%' }} />
      <div style={{ height: 9,  borderRadius: 6, background: '#f0f0f0', backgroundSize: '200% 100%', animation: 'skeletonShimmer 1.4s ease-in-out infinite', width: '55%' }} />
    </div>
    <style>{`
      @keyframes skeletonShimmer {
        0%   { background-position:  200% 0; }
        100% { background-position: -200% 0; }
      }
    `}</style>
  </div>
);

// ── Shimmer skeleton for a single stat box ────────────────────────────────────
const SkeletonStat = () => (
  <div className={styles.statItem} style={{ opacity: 0.6 }}>
    <div style={{ fontSize: 22, marginBottom: 6, opacity: 0.3 }}>▪</div>
    <div style={{ height: 22, width: 40, borderRadius: 6, background: '#eee', margin: '0 auto 4px', backgroundSize: '200% 100%', animation: 'skeletonShimmer 1.4s ease-in-out infinite' }} />
    <div style={{ height: 10, width: 60, borderRadius: 6, background: '#eee', margin: '0 auto', backgroundSize: '200% 100%', animation: 'skeletonShimmer 1.4s ease-in-out infinite' }} />
  </div>
);

// ══════════════════════════════════════════════════════════════════════════════
// VIEW ALL STATS MODAL
// ══════════════════════════════════════════════════════════════════════════════

const ViewAllStatsModal = ({ stats, onClose }) => {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.50)',
        zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16, animation: 'fadeIn 0.2s ease',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 20, width: '100%', maxWidth: 600,
          boxShadow: '0 24px 60px rgba(0,0,0,0.20)',
          maxHeight: '90vh', overflowY: 'auto',
          animation: 'slideUp 0.25s ease-out',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '20px 24px', borderBottom: '1px solid #f3f4f6',
          position: 'sticky', top: 0, background: '#fff', zIndex: 10,
          borderRadius: '20px 20px 0 0',
        }}>
          <div>
            <h3 style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 800, color: '#111' }}>
              Account Statistics
            </h3>
            <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>
              Your complete seller performance
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: '#f3f4f6', border: 'none', borderRadius: '50%',
              width: 36, height: 36, fontSize: 18, cursor: 'pointer',
              color: '#6b7280', display: 'flex', alignItems: 'center',
              justifyContent: 'center', flexShrink: 0,
            }}
          >
            ✕
          </button>
        </div>

        <div style={{ padding: '24px' }}>
          <div style={{
            background: 'linear-gradient(135deg, #f59e0b, #d97706)',
            borderRadius: 14, padding: '20px', marginBottom: 24,
            color: '#fff', textAlign: 'center',
          }}>
            <div style={{ fontSize: 48, marginBottom: 8 }}>⭐</div>
            <div style={{ fontSize: 32, fontWeight: 900, marginBottom: 4 }}>
              {stats.rating}
            </div>
            <div style={{ fontSize: 14, opacity: 0.95 }}>
              Based on {stats.reviewCount} review{stats.reviewCount !== 1 ? 's' : ''}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
            <div style={{ background: '#f8fafc', borderRadius: 14, padding: '20px', border: '1px solid #f0f0f0' }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>📦</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#111', marginBottom: 4 }}>{stats.activeListings}</div>
              <div style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>Active Listings</div>
            </div>
            <div style={{ background: '#fef2f2', borderRadius: 14, padding: '20px', border: '1px solid #fecaca' }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>❤️</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#111', marginBottom: 4 }}>{stats.totalLikes ?? 0}</div>
              <div style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>Total Likes</div>
            </div>
            <div style={{ background: '#eff6ff', borderRadius: 14, padding: '20px', border: '1px solid #dbeafe' }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>👥</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#111', marginBottom: 4 }}>{stats.followers ?? 0}</div>
              <div style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>Followers</div>
            </div>
            <div style={{ background: '#f0fdf4', borderRadius: 14, padding: '20px', border: '1px solid #bbf7d0' }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>👁️</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#111', marginBottom: 4 }}>{stats.totalViews ?? '—'}</div>
              <div style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>Total Views</div>
            </div>
          </div>

          <div style={{ marginTop: 24, padding: '16px', background: '#fafafa', borderRadius: 12, fontSize: 13, color: '#6b7280', lineHeight: 1.6 }}>
            <strong style={{ color: '#111' }}>💡 Tip:</strong> Keep your listings active and respond quickly to messages to improve your seller rating!
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn  { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// VIEW ALL ITEMS MODAL
// ══════════════════════════════════════════════════════════════════════════════

const ViewAllItemsModal = ({ items, onClose, onItemClick }) => {
  const [filter, setFilter] = useState('all');

  const filteredItems = items.filter(item => {
    if (filter === 'active') return item.status !== 'sold';
    if (filter === 'sold')   return item.status === 'sold';
    return true;
  });

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.50)',
        zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16, animation: 'fadeIn 0.2s ease',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 20, width: '100%', maxWidth: 1200,
          boxShadow: '0 24px 60px rgba(0,0,0,0.20)',
          maxHeight: '90vh', overflowY: 'auto',
          animation: 'slideUp 0.25s ease-out',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '20px 24px', borderBottom: '1px solid #f3f4f6',
          position: 'sticky', top: 0, background: '#fff', zIndex: 10,
          borderRadius: '20px 20px 0 0',
        }}>
          <div>
            <h3 style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 800, color: '#111' }}>
              My Items ({items.length})
            </h3>
            <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>All your posted listings</p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: '#f3f4f6', border: 'none', borderRadius: '50%',
              width: 36, height: 36, fontSize: 18, cursor: 'pointer',
              color: '#6b7280', display: 'flex', alignItems: 'center',
              justifyContent: 'center', flexShrink: 0,
            }}
          >
            ✕
          </button>
        </div>

        <div style={{
          padding: '16px 24px', borderBottom: '1px solid #f3f4f6',
          display: 'flex', gap: 8, position: 'sticky', top: 77,
          background: '#fff', zIndex: 9,
        }}>
          {[
            { key: 'all',    label: 'All',    count: items.length },
            { key: 'active', label: 'Active', count: items.filter(i => i.status !== 'sold').length },
            { key: 'sold',   label: 'Sold',   count: items.filter(i => i.status === 'sold').length },
          ].map(({ key, label, count }) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              style={{
                padding: '8px 16px', borderRadius: 20,
                border: filter === key ? 'none' : '1.5px solid #e5e7eb',
                background: filter === key ? 'linear-gradient(135deg, #16a34a, #15803d)' : '#fff',
                color: filter === key ? '#fff' : '#6b7280',
                fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s',
              }}
            >
              {label} ({count})
            </button>
          ))}
        </div>

        <div style={{ padding: '24px' }}>
          {filteredItems.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#9ca3af' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>📦</div>
              <div style={{ fontSize: 15, fontWeight: 600 }}>No {filter} items found</div>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}>
              {filteredItems.map(item => (
                <ItemCard
                  key={item.id}
                  product={item}
                  onClick={() => { onClose(); onItemClick(item); }}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fadeIn  { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// SELLING TAB
// ══════════════════════════════════════════════════════════════════════════════

const SellingTab = ({ user, userProducts, loading, stats, setStats, setUserProducts }) => {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showStatsModal,  setShowStatsModal]  = useState(false);
  const [showItemsModal,  setShowItemsModal]  = useState(false);

  const sortedProducts = React.useMemo(() => {
    return [...userProducts].sort((a, b) => {
      const dateA = new Date(a.date_added || a.created_at || a.createdAt || 0);
      const dateB = new Date(b.date_added || b.created_at || b.createdAt || 0);
      return dateB - dateA;
    });
  }, [userProducts]);

  const handleOpen  = (product) => setSelectedProduct(product);
  const handleClose = ()        => setSelectedProduct(null);

  const handleUpdated = (updated) => {
    setUserProducts(prev => prev.map(p => p.id === updated.id ? updated : p));
    setSelectedProduct(updated);
  };

  const handleStatusToggled = (updated) => {
    setUserProducts(prev => prev.map(p => p.id === updated.id ? updated : p));
    setSelectedProduct(updated);
    const activeCount = updated.status !== 'sold'
      ? userProducts.filter(p => p.id === updated.id ? true  : p.status !== 'sold').length
      : userProducts.filter(p => p.id === updated.id ? false : p.status !== 'sold').length;
    setStats(prev => ({ ...prev, activeListings: activeCount }));
  };

  const handleDeleted = (deleted) => {
    setUserProducts(prev => prev.filter(p => p.id !== deleted.id));
    const wasActive   = deleted.status !== 'sold';
    const deletedLikes = deleted.like_count || deleted.likes_count || deleted.likes || 0;
    setStats(prev => ({
      ...prev,
      activeListings: wasActive ? Math.max(0, prev.activeListings - 1) : prev.activeListings,
      totalLikes:     Math.max(0, (prev.totalLikes || 0) - deletedLikes),
    }));
  };

  // ── Stats bar — always rendered; shows skeleton boxes while loading ─────────
  const statsReady = !loading || stats.activeListings > 0;

  return (
    <>
      {/* ── Account Statistics card ──────────────────────────────────────────── */}
      <div className={styles.statsCard}>
        <div className={styles.statsHeader}>
          <h3 className={styles.statsTitle}>
            Account Statistics
            {!loading && (
              <span className={styles.starsRating}>
                {'⭐'.repeat(Math.round(stats.rating || 0))} {stats.rating}
              </span>
            )}
          </h3>
          <button
            className={styles.viewAllBtn}
            onClick={() => setShowStatsModal(true)}
            disabled={loading}
          >
            View All <span>›</span>
          </button>
        </div>

        <div className={styles.statsGrid}>
          {loading && !statsReady ? (
            // Skeleton stat boxes during first load
            <>
              <SkeletonStat />
              <SkeletonStat />
              <SkeletonStat />
              <SkeletonStat />
            </>
          ) : (
            <>
              <div className={styles.statItem}>
                <div className={styles.statIcon}>📦</div>
                <div className={styles.statValue}>{stats.activeListings}</div>
                <div className={styles.statLabel}>Active Listings</div>
              </div>
              <div className={styles.statItem}>
                <div className={styles.statIcon}>❤️</div>
                <div className={styles.statValue}>{stats.totalLikes ?? 0}</div>
                <div className={styles.statLabel}>Likes</div>
              </div>
              <div className={styles.statItem}>
                <div className={styles.statIcon}>⭐</div>
                <div className={styles.statValue}>{stats.rating}</div>
                <div className={styles.statLabel}>✓ <span className={styles.reviewCount}>({stats.reviewCount} Reviews)</span></div>
              </div>
              <div className={styles.statItem}>
                <div className={styles.statIcon}>👥</div>
                <div className={styles.statValue}>{stats.followers ?? 0}</div>
                <div className={styles.statLabel}>Followers</div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── My Items card ─────────────────────────────────────────────────────── */}
      <div className={styles.myItemsCard}>
        <div className={styles.myItemsHeader}>
          <h3 className={styles.myItemsTitle}>
            My Items ({loading ? '…' : sortedProducts.length})
          </h3>
          <button
            className={styles.viewAllBtn}
            onClick={() => setShowItemsModal(true)}
            disabled={loading}
          >
            View All <span>›</span>
          </button>
        </div>

        {loading ? (
          // ── Skeleton grid — layout is immediate, no spinner blocking ─────────
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
            gap: '16px',
            padding: '4px 0 8px',
          }}>
            {[...Array(6)].map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : sortedProducts.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>📦</div>
            <h4>No items posted yet</h4>
            <p>Start selling by posting your first ad!</p>
            <button
              className={styles.postAdBtn}
              onClick={() => window.location.href = '/post-ad'}
            >
              Post an Ad
            </button>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
            gap: '16px',
            padding: '4px 0 8px',
          }}>
            {sortedProducts.slice(0, 6).map(product => (
              <ItemCard
                key={product.id}
                product={product}
                onClick={() => handleOpen(product)}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Modals ────────────────────────────────────────────────────────────── */}
      {selectedProduct && (
        <EditAdModal
          product={selectedProduct}
          onClose={handleClose}
          onUpdated={handleUpdated}
          onStatusToggled={handleStatusToggled}
          onDeleted={handleDeleted}
        />
      )}

      {showStatsModal && (
        <ViewAllStatsModal
          stats={stats}
          onClose={() => setShowStatsModal(false)}
        />
      )}

      {showItemsModal && (
        <ViewAllItemsModal
          items={sortedProducts}
          onClose={() => setShowItemsModal(false)}
          onItemClick={handleOpen}
        />
      )}
    </>
  );
};

export default SellingTab;



































// /**
//  * selling/SellingTab.jsx
//  * ─────────────────────────────────────────────────────────────────────────────
//  * Main selling dashboard — shows the user's listings grid with stats.
//  *
//  * WHAT LIVES HERE:
//  *   - Stats bar (active listings, likes, followers, rating)
//  *   - "View All Stats" modal trigger
//  *   - "View All Items" modal trigger  
//  *   - Product grid using ItemCard components
//  *   - Opens EditAdModal on card click
//  *
//  * SUB-COMPONENTS (each in their own file):
//  *   ItemCard.jsx          — single listing card
//  *   EditAdModal.jsx       — edit/delete/status toggle modal (~480 lines)
//  *   sellingUtils.js       — shared constants and helpers
//  *
//  * PROPS (from ProfilePage sharedProps):
//  *   user         {object}   — current user
//  *   userProducts {array}    — user's ads from ProfilePage.loadUserData
//  *   loading      {boolean}  — true while initial data loads
//  *   stats        {object}   — { activeListings, totalLikes, followers, rating, reviewCount }
//  *   setStats     {function} — update stats after edit/delete
//  *   setUserProducts {function} — update products after edit/delete
//  * ─────────────────────────────────────────────────────────────────────────────
//  */

// import React, { useState } from 'react';
// import styles from './SellingTab.module.css';
// import ItemCard    from './ItemCard';
// import EditAdModal from './EditAdModal';
// import { StarRating } from './sellingUtils';

// const ViewAllStatsModal = ({ stats, onClose }) => {
//   return (
//     <div
//       onClick={onClose}
//       style={{
//         position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.50)',
//         zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center',
//         padding: 16, animation: 'fadeIn 0.2s ease',
//       }}
//     >
//       <div
//         onClick={e => e.stopPropagation()}
//         style={{
//           background: '#fff', borderRadius: 20, width: '100%', maxWidth: 600,
//           boxShadow: '0 24px 60px rgba(0,0,0,0.20)',
//           maxHeight: '90vh', overflowY: 'auto',
//           animation: 'slideUp 0.25s ease-out',
//         }}
//       >
//         <div style={{
//           display: 'flex', alignItems: 'center', justifyContent: 'space-between',
//           padding: '20px 24px', borderBottom: '1px solid #f3f4f6',
//           position: 'sticky', top: 0, background: '#fff', zIndex: 10,
//           borderRadius: '20px 20px 0 0',
//         }}>
//           <div>
//             <h3 style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 800, color: '#111' }}>
//               Account Statistics
//             </h3>
//             <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>
//               Your complete seller performance
//             </p>
//           </div>
//           <button
//             onClick={onClose}
//             style={{
//               background: '#f3f4f6', border: 'none', borderRadius: '50%',
//               width: 36, height: 36, fontSize: 18, cursor: 'pointer',
//               color: '#6b7280', display: 'flex', alignItems: 'center',
//               justifyContent: 'center', flexShrink: 0,
//             }}
//           >
//             ✕
//           </button>
//         </div>

//         <div style={{ padding: '24px' }}>
//           <div style={{
//             background: 'linear-gradient(135deg, #f59e0b, #d97706)',
//             borderRadius: 14, padding: '20px', marginBottom: 24,
//             color: '#fff', textAlign: 'center',
//           }}>
//             <div style={{ fontSize: 48, marginBottom: 8 }}>⭐</div>
//             <div style={{ fontSize: 32, fontWeight: 900, marginBottom: 4 }}>
//               {stats.rating}
//             </div>
//             <div style={{ fontSize: 14, opacity: 0.95 }}>
//               Based on {stats.reviewCount} review{stats.reviewCount !== 1 ? 's' : ''}
//             </div>
//           </div>

//           <div style={{
//             display: 'grid',
//             gridTemplateColumns: 'repeat(2, 1fr)',
//             gap: 16,
//           }}>
//             <div style={{
//               background: '#f8fafc', borderRadius: 14,
//               padding: '20px', border: '1px solid #f0f0f0',
//             }}>
//               <div style={{ fontSize: 36, marginBottom: 8 }}>📦</div>
//               <div style={{ fontSize: 28, fontWeight: 800, color: '#111', marginBottom: 4 }}>
//                 {stats.activeListings}
//               </div>
//               <div style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>
//                 Active Listings
//               </div>
//             </div>

//             <div style={{
//               background: '#fef2f2', borderRadius: 14,
//               padding: '20px', border: '1px solid #fecaca',
//             }}>
//               <div style={{ fontSize: 36, marginBottom: 8 }}>❤️</div>
//               <div style={{ fontSize: 28, fontWeight: 800, color: '#111', marginBottom: 4 }}>
//                 {stats.totalLikes ?? 0}
//               </div>
//               <div style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>
//                 Total Likes
//               </div>
//             </div>

//             <div style={{
//               background: '#eff6ff', borderRadius: 14,
//               padding: '20px', border: '1px solid #dbeafe',
//             }}>
//               <div style={{ fontSize: 36, marginBottom: 8 }}>👥</div>
//               <div style={{ fontSize: 28, fontWeight: 800, color: '#111', marginBottom: 4 }}>
//                 {stats.followers ?? 0}
//               </div>
//               <div style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>
//                 Followers
//               </div>
//             </div>

//             <div style={{
//               background: '#f0fdf4', borderRadius: 14,
//               padding: '20px', border: '1px solid #bbf7d0',
//             }}>
//               <div style={{ fontSize: 36, marginBottom: 8 }}>👁️</div>
//               <div style={{ fontSize: 28, fontWeight: 800, color: '#111', marginBottom: 4 }}>
//                 {stats.totalViews ?? '—'}
//               </div>
//               <div style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>
//                 Total Views
//               </div>
//             </div>
//           </div>

//           <div style={{
//             marginTop: 24, padding: '16px', background: '#fafafa',
//             borderRadius: 12, fontSize: 13, color: '#6b7280',
//             lineHeight: 1.6,
//           }}>
//             <strong style={{ color: '#111' }}>💡 Tip:</strong> Keep your listings active and respond quickly to messages to improve your seller rating!
//           </div>
//         </div>
//       </div>

//       <style>{`
//         @keyframes fadeIn {
//           from { opacity: 0; }
//           to { opacity: 1; }
//         }
//         @keyframes slideUp {
//           from { opacity: 0; transform: translateY(20px); }
//           to { opacity: 1; transform: translateY(0); }
//         }
//       `}</style>
//     </div>
//   );
// };

// // ══════════════════════════════════════════════════════════════════════════════
// // VIEW ALL ITEMS MODAL
// // ══════════════════════════════════════════════════════════════════════════════

// const ViewAllItemsModal = ({ items, onClose, onItemClick }) => {
//   const [filter, setFilter] = useState('all');

//   const filteredItems = items.filter(item => {
//     if (filter === 'active') return item.status !== 'sold';
//     if (filter === 'sold') return item.status === 'sold';
//     return true;
//   });

//   return (
//     <div
//       onClick={onClose}
//       style={{
//         position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.50)',
//         zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center',
//         padding: 16, animation: 'fadeIn 0.2s ease',
//       }}
//     >
//       <div
//         onClick={e => e.stopPropagation()}
//         style={{
//           background: '#fff', borderRadius: 20, width: '100%', maxWidth: 1200,
//           boxShadow: '0 24px 60px rgba(0,0,0,0.20)',
//           maxHeight: '90vh', overflowY: 'auto',
//           animation: 'slideUp 0.25s ease-out',
//         }}
//       >
//         <div style={{
//           display: 'flex', alignItems: 'center', justifyContent: 'space-between',
//           padding: '20px 24px', borderBottom: '1px solid #f3f4f6',
//           position: 'sticky', top: 0, background: '#fff', zIndex: 10,
//           borderRadius: '20px 20px 0 0',
//         }}>
//           <div>
//             <h3 style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 800, color: '#111' }}>
//               My Items ({items.length})
//             </h3>
//             <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>
//               All your posted listings
//             </p>
//           </div>
//           <button
//             onClick={onClose}
//             style={{
//               background: '#f3f4f6', border: 'none', borderRadius: '50%',
//               width: 36, height: 36, fontSize: 18, cursor: 'pointer',
//               color: '#6b7280', display: 'flex', alignItems: 'center',
//               justifyContent: 'center', flexShrink: 0,
//             }}
//           >
//             ✕
//           </button>
//         </div>

//         <div style={{
//           padding: '16px 24px', borderBottom: '1px solid #f3f4f6',
//           display: 'flex', gap: 8, position: 'sticky', top: 77,
//           background: '#fff', zIndex: 9,
//         }}>
//           {[
//             { key: 'all', label: 'All', count: items.length },
//             { key: 'active', label: 'Active', count: items.filter(i => i.status !== 'sold').length },
//             { key: 'sold', label: 'Sold', count: items.filter(i => i.status === 'sold').length },
//           ].map(({ key, label, count }) => (
//             <button
//               key={key}
//               onClick={() => setFilter(key)}
//               style={{
//                 padding: '8px 16px', borderRadius: 20,
//                 border: filter === key ? 'none' : '1.5px solid #e5e7eb',
//                 background: filter === key
//                   ? 'linear-gradient(135deg, #16a34a, #15803d)'
//                   : '#fff',
//                 color: filter === key ? '#fff' : '#6b7280',
//                 fontSize: 13, fontWeight: 600, cursor: 'pointer',
//                 transition: 'all 0.15s',
//               }}
//             >
//               {label} ({count})
//             </button>
//           ))}
//         </div>

//         <div style={{ padding: '24px' }}>
//           {filteredItems.length === 0 ? (
//             <div style={{
//               textAlign: 'center', padding: '60px 20px',
//               color: '#9ca3af',
//             }}>
//               <div style={{ fontSize: 48, marginBottom: 12 }}>📦</div>
//               <div style={{ fontSize: 15, fontWeight: 600 }}>
//                 No {filter} items found
//               </div>
//             </div>
//           ) : (
//             <div style={{
//               display: 'grid',
//               gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
//               gap: 16,
//             }}>
//               {filteredItems.map(item => (
//                 <ItemCard
//                   key={item.id}
//                   product={item}
//                   onClick={() => {
//                     onClose();
//                     onItemClick(item);
//                   }}
//                 />
//               ))}
//             </div>
//           )}
//         </div>
//       </div>

//       <style>{`
//         @keyframes fadeIn {
//           from { opacity: 0; }
//           to { opacity: 1; }
//         }
//         @keyframes slideUp {
//           from { opacity: 0; transform: translateY(20px); }
//           to { opacity: 1; transform: translateY(0); }
//         }
//       `}</style>
//     </div>
//   );
// };

// // ══════════════════════════════════════════════════════════════════════════════
// // EDIT AD MODAL - WITH MEDIA CAROUSEL
// // ══════════════════════════════════════════════════════════════════════════════

// const SellingTab = ({ user, userProducts, loading, stats, setStats, setUserProducts }) => {
//   const [selectedProduct, setSelectedProduct] = useState(null);
//   const [showStatsModal, setShowStatsModal] = useState(false);
//   const [showItemsModal, setShowItemsModal] = useState(false);

//   const sortedProducts = React.useMemo(() => {
//     return [...userProducts].sort((a, b) => {
//       const dateA = new Date(a.date_added || a.created_at || a.createdAt || 0);
//       const dateB = new Date(b.date_added || b.created_at || b.createdAt || 0);
//       return dateB - dateA;
//     });
//   }, [userProducts]);

//   const handleOpen = (product) => setSelectedProduct(product);
//   const handleClose = () => setSelectedProduct(null);

//   const handleUpdated = (updated) => {
//     setUserProducts(prev => prev.map(p => p.id === updated.id ? updated : p));
//     setSelectedProduct(updated);
//   };

//   const handleStatusToggled = (updated) => {
//     setUserProducts(prev => prev.map(p => p.id === updated.id ? updated : p));
//     setSelectedProduct(updated);
//     const activeCount = updated.status !== 'sold'
//       ? userProducts.filter(p => p.id === updated.id ? true : p.status !== 'sold').length
//       : userProducts.filter(p => p.id === updated.id ? false : p.status !== 'sold').length;
//     setStats(prev => ({ ...prev, activeListings: activeCount }));
//   };

//   const handleDeleted = (deleted) => {
//     setUserProducts(prev => prev.filter(p => p.id !== deleted.id));
//     const wasActive = deleted.status !== 'sold';
//     const deletedLikes = deleted.like_count || deleted.likes_count || deleted.likes || 0;
//     setStats(prev => ({
//       ...prev,
//       activeListings: wasActive ? Math.max(0, prev.activeListings - 1) : prev.activeListings,
//       totalLikes: Math.max(0, (prev.totalLikes || 0) - deletedLikes),
//     }));
//   };

//   return (
//     <>
//       <div className={styles.statsCard}>
//         <div className={styles.statsHeader}>
//           <h3 className={styles.statsTitle}>
//             Account Statistics
//             <span className={styles.starsRating}>
//               {'⭐'.repeat(Math.round(stats.rating || 0))} {stats.rating}
//             </span>
//           </h3>
//           <button
//             className={styles.viewAllBtn}
//             onClick={() => setShowStatsModal(true)}
//           >
//             View All <span>›</span>
//           </button>
//         </div>
//         <div className={styles.statsGrid}>
//           <div className={styles.statItem}><div className={styles.statIcon}>📦</div><div className={styles.statValue}>{stats.activeListings}</div><div className={styles.statLabel}>Active Listings</div></div>
//           <div className={styles.statItem}><div className={styles.statIcon}>❤️</div><div className={styles.statValue}>{stats.totalLikes ?? 0}</div><div className={styles.statLabel}>Likes</div></div>
//           <div className={styles.statItem}><div className={styles.statIcon}>⭐</div><div className={styles.statValue}>{stats.rating}</div><div className={styles.statLabel}>✓ <span className={styles.reviewCount}>({stats.reviewCount} Reviews)</span></div></div>
//           <div className={styles.statItem}><div className={styles.statIcon}>👥</div><div className={styles.statValue}>{stats.followers ?? 0}</div><div className={styles.statLabel}>Followers</div></div>
//         </div>
//       </div>

//       <div className={styles.myItemsCard}>
//         <div className={styles.myItemsHeader}>
//           <h3 className={styles.myItemsTitle}>My Items ({sortedProducts.length})</h3>
//           <button
//             className={styles.viewAllBtn}
//             onClick={() => setShowItemsModal(true)}
//           >
//             View All <span>›</span>
//           </button>
//         </div>

//         {loading ? (
//           <div className={styles.loadingState}>
//             <div className={styles.spinner} />
//             <p>Loading your items...</p>
//           </div>
//         ) : sortedProducts.length === 0 ? (
//           <div className={styles.emptyState}>
//             <div className={styles.emptyIcon}>📦</div>
//             <h4>No items posted yet</h4>
//             <p>Start selling by posting your first ad!</p>
//             <button className={styles.postAdBtn} onClick={() => window.location.href = '/post-ad'}>Post an Ad</button>
//           </div>
//         ) : (
//           <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '16px', padding: '4px 0 8px' }}>
//             {sortedProducts.slice(0, 6).map(product => (
//               <ItemCard key={product.id} product={product} onClick={() => handleOpen(product)} />
//             ))}
//           </div>
//         )}
//       </div>

//       {selectedProduct && (
//         <EditAdModal
//           product={selectedProduct}
//           onClose={handleClose}
//           onUpdated={handleUpdated}
//           onStatusToggled={handleStatusToggled}
//           onDeleted={handleDeleted}
//         />
//       )}

//       {showStatsModal && (
//         <ViewAllStatsModal
//           stats={stats}
//           onClose={() => setShowStatsModal(false)}
//         />
//       )}

//       {showItemsModal && (
//         <ViewAllItemsModal
//           items={sortedProducts}
//           onClose={() => setShowItemsModal(false)}
//           onItemClick={handleOpen}
//         />
//       )}
//     </>
//   );
// };

// export default SellingTab;














